import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi, guestPaymentsApi } from '../services/api';
import { toast } from '../stores/toastStore';
import StatCard from '../components/StatCard';
import SalesChart from '../components/SalesChart';
import LiveSalesPage from './LiveSalesPage';
import api from '../services/api';
import {
  DollarSign,
  ShoppingCart,
  Users,
  CreditCard,
  Banknote,
  Calendar,
  Moon,
  Search,
  UtensilsCrossed,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import clsx from 'clsx';

type DateRangeOption = '7d' | '30d' | '90d' | 'custom';

// POS-connected accounts (Toast/Square) get the live sales view; classic
// Mise-POS restaurants keep the check-based analytics. The gate is its own
// component so each branch keeps a stable hook order.
export default function SalesPage() {
  const { data: toastStatus, isLoading: toastLoading } = useQuery({
    queryKey: ['toast-status-gate'],
    queryFn: async () => (await api.get('/v1/integrations/toast/status')).data?.data,
    staleTime: 60_000,
  });
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['owner-overview'],
    queryFn: async () => (await api.get('/v1/owner/overview')).data?.data,
    staleTime: 60_000,
    retry: 1,
  });
  if (toastLoading) return null;
  if (toastStatus?.connected) return <LiveSalesPage />;
  if (overviewLoading) return null;
  // A hub venue (commissary) rings nothing itself — if the company's stores
  // have POS sales, show them rather than an empty analytics page.
  const companySales = (overview?.venues || []).some((v: any) => (v.salesWeekCents || 0) > 0);
  if (companySales) return <LiveSalesPage />;
  return <ClassicSalesPage />;
}

function ClassicSalesPage() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('7d');

  const getDateRange = () => {
    const end = new Date();
    let start: Date;

    switch (dateRange) {
      case '7d':
        start = subDays(end, 7);
        break;
      case '30d':
        start = subDays(end, 30);
        break;
      case '90d':
        start = subDays(end, 90);
        break;
      default:
        start = subDays(end, 7);
    }

    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  };

  const { data: salesData, isLoading } = useQuery({
    queryKey: ['sales-report', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getSalesReport(getDateRange());
      return response.data?.data || response.data;
    },
  });

  // Fetch server performance data
  const { data: serverData } = useQuery({
    queryKey: ['server-performance', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getServerPerformance(getDateRange());
      return response.data?.data || response.data;
    },
  });

  // Fetch kitchen performance for peak hours
  const { data: kitchenData } = useQuery({
    queryKey: ['kitchen-performance', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getKitchenPerformance(getDateRange());
      return response.data?.data || response.data;
    },
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Payment split from the API (same payments EOD counts) — no invented rows
  const byMethod: Record<string, number> = salesData?.paymentsByMethod || {};
  const methodTotal = Object.values(byMethod).reduce((a, b) => a + b, 0);
  const LABELS: Record<string, string> = { card: 'Credit Card', credit_card: 'Credit Card', cash: 'Cash', apple_pay: 'Apple Pay', gift_card: 'Gift Card', other: 'Other' };
  const realPaymentBreakdown = Object.entries(byMethod)
    .sort((a, b) => b[1] - a[1])
    .map(([m, amt]) => ({ method: LABELS[m] || m.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), amount: amt, percentage: methodTotal > 0 ? Math.round((amt / methodTotal) * 100) : 0 }));

  // Map API data to expected format (API returns summary + salesByPeriod)
  const apiSummary = salesData?.summary || {};
  const apiSalesByPeriod = salesData?.salesByPeriod || [];

  // Convert salesByPeriod to dailySales format
  const dailySalesFromApi = apiSalesByPeriod.map((day: any) => ({
    label: day.period?.split('-').pop() || day.period,
    revenue: day.revenue || 0,
    orders: day.checkCount || 0,
  }));

  // Map server performance data
  const apiServers = serverData?.servers || [];
  const topServersFromApi = apiServers.slice(0, 4).map((s: any) => ({
    name: s.serverName || 'Unknown',
    sales: s.totalSales || 0,
    orders: s.checks || 0,
  }));

  // Map kitchen performance data for peak hours
  const apiOrdersByHour = kitchenData?.ordersByHour || [];
  const peakHoursFromApi = apiOrdersByHour
    .filter((h: any) => h.count > 0)
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 5)
    .map((h: any) => ({
      hour: `${h.hour % 12 === 0 ? 12 : h.hour % 12} ${h.hour >= 12 ? 'PM' : 'AM'}`,
      revenue: (apiSummary.totalRevenue || 0) * (h.count / (apiOrdersByHour.reduce((sum: number, x: any) => sum + x.count, 0) || 1)),
      count: h.count,
    }));

  // Use real API data only - show zeros if no data
  const data = {
    totalRevenue: apiSummary.totalRevenue ?? 0,
    totalOrders: apiSummary.totalChecks ?? 0,
    averageTicket: apiSummary.averageCheck ?? 0,
    guestCount: apiSummary.totalChecks ?? 0, // Use check count as proxy for guests
    revenueTrend: 0, // Trend calculation not yet implemented
    ordersTrend: 0,
    ticketTrend: 0,
    guestTrend: 0,
    dailySales: dailySalesFromApi,
    paymentBreakdown: realPaymentBreakdown,
    topServers: topServersFromApi,
    peakHours: peakHoursFromApi,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales</h1>
          <p className="text-slate-500">Revenue and order analytics</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Close-out lives with the money, too (also on the Shifts day view) */}
          <Link
            to="/end-of-day"
            className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 flex items-center gap-1.5"
          >
            <Moon className="w-4 h-4" />
            End of day
          </Link>
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={clsx(
                'px-4 py-2 rounded-lg font-medium transition-colors',
                dateRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              )}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(data.totalRevenue)}
          change={data.revenueTrend}
          icon={<DollarSign className="w-6 h-6 text-primary-600" />}
          iconBg="bg-primary-100"
        />
        <StatCard
          title="Total Orders"
          value={data.totalOrders}
          change={data.ordersTrend}
          icon={<ShoppingCart className="w-6 h-6 text-success-600" />}
          iconBg="bg-success-100"
        />
        <StatCard
          title="Average Ticket"
          value={formatCurrency(data.averageTicket)}
          change={data.ticketTrend}
          icon={<CreditCard className="w-6 h-6 text-warning-600" />}
          iconBg="bg-warning-100"
        />
        <StatCard
          title="Guest Count"
          value={data.guestCount}
          change={data.guestTrend}
          icon={<Users className="w-6 h-6 text-slate-600" />}
          iconBg="bg-slate-100"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Sales Chart */}
        <div className="lg:col-span-2 card p-6">
          <h2 className="text-lg font-semibold mb-4">Daily Sales</h2>
          <SalesChart data={data.dailySales} />
        </div>

        {/* Payment Breakdown — rendered only when real payments exist */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Payment Methods</h2>
          {data.paymentBreakdown.length === 0 && (
            <p className="text-sm text-slate-400">Shows card vs cash as payments flow through Mise — nothing recorded in this window yet.</p>
          )}
          <div className="space-y-4">
            {data.paymentBreakdown.map((payment) => (
              <div key={payment.method}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{payment.method}</span>
                  <span className="text-sm text-slate-500">
                    {formatCurrency(payment.amount)}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full"
                    style={{ width: `${payment.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Server Performance and Peak Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Servers */}
        <div className="card">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold">Top Servers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="table-header">Server</th>
                  <th className="table-header text-right">Sales</th>
                  <th className="table-header text-right">Orders</th>
                  <th className="table-header text-right">Avg/Order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.topServers.map((server: { name: string; sales: number; orders: number }, index: number) => (
                  <tr key={server.name}>
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="font-medium">{server.name}</span>
                      </div>
                    </td>
                    <td className="table-cell text-right font-semibold">
                      {formatCurrency(server.sales)}
                    </td>
                    <td className="table-cell text-right">{server.orders}</td>
                    <td className="table-cell text-right text-slate-500">
                      {formatCurrency(Math.round(server.sales / server.orders))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Peak Hours */}
        <div className="card">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold">Peak Hours</h2>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {data.peakHours.map((hour: { hour: string; orders: number; maxOrders: number; revenue: number }, index: number) => (
                <div key={hour.hour} className="flex items-center gap-4">
                  <span className="w-16 text-sm font-medium">{hour.hour}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-lg',
                        index === 0 && 'bg-primary-300',
                        index === 1 && 'bg-primary-400',
                        index === 2 && 'bg-primary-500',
                        index === 3 && 'bg-primary-600',
                        index === 4 && 'bg-primary-500'
                      )}
                      style={{
                        width: `${Math.min(100, (hour.revenue / Math.max(...data.peakHours.map((x: any) => x.revenue), 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="w-20 text-sm text-right">
                    {formatCurrency(hour.revenue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Find any item's numbers for the selected range */}
      <ItemSalesCard rangeKey={dateRange} getDateRange={getDateRange} formatCurrency={formatCurrency} />

      {/* Recent payments — view and refund without leaving Mise */}
      <RecentPaymentsCard />
    </div>
  );
}

// ===========================================
// ITEM SALES SEARCH
// ===========================================
// "How did the almond croissant do this month?" — type a name, get units and
// revenue for the selected range, right where sales live (the Reports page has
// the same lookup for deep dives). Data comes from the same closed checks as
// the rest of this page.
function ItemSalesCard({
  rangeKey,
  getDateRange,
  formatCurrency,
}: {
  rangeKey: string;
  getDateRange: () => { startDate: string; endDate: string };
  formatCurrency: (cents: number) => string;
}) {
  const [query, setQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['item-sales-search', rangeKey],
    queryFn: async () => {
      const response = await analyticsApi.getMenuPerformance({ ...getDateRange(), limit: 500 });
      return response.data?.data || response.data;
    },
  });

  const items = (data?.topSellers || []) as { menuItemId: string; name: string; quantity: number; revenue: number }[];
  const q = query.trim().toLowerCase();
  const matches = q ? items.filter((i) => (i.name || '').toLowerCase().includes(q)) : items.slice(0, 8);

  return (
    <div className="card">
      <div className="p-4 border-b border-slate-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Item Sales</h2>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search an item…"
            className="input w-full pl-9"
          />
        </div>
      </div>
      <div className="p-4">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-slate-400">Loading item sales…</p>
        ) : matches.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            <UtensilsCrossed className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {q
                ? `Nothing sold matching “${query.trim()}” in this period.`
                : 'No item sales in this period yet — numbers appear as checks close.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {!q && (
              <p className="pb-2 text-xs text-slate-400">Top sellers for this period — search to find any item</p>
            )}
            {matches.map((item) => (
              <div key={item.menuItemId || item.name} className="py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-slate-800 truncate">{item.name}</span>
                <span className="flex items-center gap-6 shrink-0 text-sm">
                  <span className="text-slate-500">{item.quantity} sold</span>
                  <span className="font-medium text-slate-900 w-20 text-right">{formatCurrency(item.revenue)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================
// RECENT PAYMENTS + REFUNDS
// ===========================================
// Refunds are manager-authorized on the backend; if the API asks for a
// manager PIN we reveal a PIN field instead of failing. Connect-routed
// refunds are funded from the restaurant's own balance (reverse_transfer).
function RecentPaymentsCard() {
  const queryClient = useQueryClient();
  const [refunding, setRefunding] = useState<any | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [needsPin, setNeedsPin] = useState(false);

  const { data: paymentsData, isLoading } = useQuery({
    queryKey: ['guest-payments'],
    queryFn: async () => {
      const response = await guestPaymentsApi.list(25);
      return response.data?.data as any[];
    },
  });

  const refundMutation = useMutation({
    mutationFn: async () => {
      if (!refunding) return;
      const amountCents = Math.round(parseFloat(refundAmount || '0') * 100);
      return guestPaymentsApi.refund(refunding.id, {
        amount: amountCents,
        reason: refundReason.trim(),
        ...(managerPin ? { managerPin } : {}),
      });
    },
    onSuccess: () => {
      toast.success('Refund issued — the guest will see it in a few business days.');
      closeModal();
      queryClient.invalidateQueries({ queryKey: ['guest-payments'] });
    },
    onError: (err: any) => {
      const e = err?.response?.data?.error;
      if (e?.code === 'MANAGER_AUTH_REQUIRED') {
        setNeedsPin(true);
        toast.error('A manager PIN is needed to authorize this refund.');
        return;
      }
      toast.error(e?.message || 'Could not issue the refund — please try again.');
    },
  });

  const closeModal = () => {
    setRefunding(null);
    setRefundAmount('');
    setRefundReason('');
    setManagerPin('');
    setNeedsPin(false);
  };

  const money = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;
  const payments = paymentsData || [];

  const openRefund = (p: any) => {
    setRefunding(p);
    setRefundAmount(((p.totalAmountCents - p.refundedAmountCents) / 100).toFixed(2));
  };

  return (
    <div className="card p-6 mt-6">
      <h2 className="text-lg font-semibold mb-4">Recent Payments</h2>
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading payments…</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-slate-500">No payments yet — they'll appear here as checks are paid.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {payments.map((p) => {
            const remaining = p.totalAmountCents - p.refundedAmountCents;
            const fullyRefunded = remaining <= 0;
            return (
              <div key={p.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    {p.checkNumber ? `Check #${p.checkNumber}` : 'Payment'} · {p.method === 'card_present' ? 'Card' : p.method === 'cash' ? 'Cash' : p.method}
                  </p>
                  <p className="text-xs text-slate-500">
                    {p.processedAt ? new Date(p.processedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    {p.tipAmountCents > 0 && <> · {money(p.tipAmountCents)} tip</>}
                    {p.refundedAmountCents > 0 && (
                      <span className="text-amber-600"> · {money(p.refundedAmountCents)} refunded</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-semibold text-slate-900">{money(p.totalAmountCents)}</span>
                  {!fullyRefunded ? (
                    <button
                      onClick={() => openRefund(p)}
                      className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Refund
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">Refunded</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Refund modal */}
      {refunding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Issue a refund</h3>
            <p className="text-sm text-slate-500 mb-4">
              {refunding.checkNumber ? `Check #${refunding.checkNumber}` : 'This payment'} — up to{' '}
              {money(refunding.totalAmountCents - refunding.refundedAmountCents)} available.
            </p>

            <label className="block text-sm font-medium text-slate-700 mb-1">Amount ($)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              className="input w-full mb-3"
            />

            <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
            <input
              type="text"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="e.g. dish sent back"
              className="input w-full mb-3"
            />

            {needsPin && (
              <>
                <label className="block text-sm font-medium text-slate-700 mb-1">Manager PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={managerPin}
                  onChange={(e) => setManagerPin(e.target.value)}
                  className="input w-full mb-3"
                />
              </>
            )}

            <div className="flex gap-3 mt-2">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => refundMutation.mutate()}
                disabled={
                  refundMutation.isPending ||
                  !refundReason.trim() ||
                  !(parseFloat(refundAmount) > 0) ||
                  Math.round(parseFloat(refundAmount) * 100) > refunding.totalAmountCents - refunding.refundedAmountCents
                }
                className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white font-medium disabled:opacity-50"
              >
                {refundMutation.isPending ? 'Refunding…' : 'Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
